import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, User, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const CommunicationHub = () => {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (selectedCandidate) {
      fetchMessages(selectedCandidate.id);
    }
  }, [selectedCandidate]);

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/candidates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchMessages = async (candidateId) => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/messages/${candidateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedCandidate) return;

    setIsSending(true);
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: selectedCandidate.id,
          content: newMessage
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(selectedCandidate.id);
      }
    } catch (error) {
      console.error('Send message error:', error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="w-8 h-8 text-green-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">Communication Hub</h2>
          <p className="text-gray-400">Message candidates directly</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 overflow-y-auto" style={{maxHeight: '600px'}}>
          <h3 className="text-lg font-semibold text-white mb-4">Candidates</h3>
          <div className="space-y-2">
            {candidates.map(candidate => (
              <button
                key={candidate.id}
                onClick={() => setSelectedCandidate(candidate)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  selectedCandidate?.id === candidate.id
                    ? 'bg-cyan-500/20 border border-cyan-500/50'
                    : 'bg-gray-700/50 hover:bg-gray-700'
                }`}
              >
                <p className="text-white font-semibold">{candidate.name}</p>
                <p className="text-xs text-gray-400">{candidate.job_title}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 bg-gray-800/50 border border-gray-700 rounded-xl flex flex-col" style={{height: '600px'}}>
          {selectedCandidate ? (
            <>
              <div className="p-4 border-b border-gray-700">
                <p className="text-lg font-semibold text-white">{selectedCandidate.name}</p>
                <p className="text-sm text-gray-400">{selectedCandidate.email}</p>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${
                      msg.sender_type === 'recruiter' ? 'justify-end' : 'justify-start'
                    }`}>
                      <div className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_type === 'recruiter'
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'bg-gray-700 text-gray-100'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
                    disabled={isSending}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isSending}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-6"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>Select a candidate to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunicationHub;
